/** Read-only progress queries over the player state (no mutation, no UI). */
import { upgradeShortfall, type UpgradeCost } from '@mp/core';
import { cityShops, economy, shopDef, type ShopDef, type UpgradeDef } from '../config/content.js';
import type { PlayerState } from './state.js';

export function levelId(city: string, n: number): string {
  return `${city}-${String(n).padStart(3, '0')}`;
}

export function levelNumberOf(id: string): number {
  const m = /-(\d+)$/.exec(id);
  return m ? Number(m[1]) : 0;
}

export function isCompleted(s: PlayerState, id: string): boolean {
  return (s.levels[id]?.bestStars ?? 0) > 0;
}

/** Highest completed level number in the city (levels are played in order). */
export function highestCompleted(s: PlayerState, city = s.city): number {
  let best = 0;
  for (const [id, lv] of Object.entries(s.levels)) {
    if (!id.startsWith(city + '-') || lv.bestStars <= 0) continue;
    best = Math.max(best, levelNumberOf(id));
  }
  return best;
}

/** The level to play next in a city whose levels are numbered first…maxLevel (packs may start at 301). */
export function currentLevelNumber(s: PlayerState, maxLevel: number, city = s.city, first = 1): number {
  return Math.min(Math.max(first, highestCompleted(s, city) + 1), Math.max(first, maxLevel));
}

export function totalStars(s: PlayerState): number {
  let n = 0;
  for (const lv of Object.values(s.levels)) n += lv.bestStars;
  return n;
}

export function upgradeCount(s: PlayerState, shopId: string): number {
  return Object.keys(s.shops[shopId]?.upgrades ?? {}).length;
}

export function isShopComplete(s: PlayerState, shop: ShopDef): boolean {
  return upgradeCount(s, shop.id) >= shop.upgrades.length;
}

/** A shop opens for renovation when the previous one is complete and the star total is reached. */
export function isShopUnlocked(s: PlayerState, shopId: string): boolean {
  const shop = shopDef(shopId);
  if (!shop) return false;
  const list = cityShops(shop.city);
  const idx = list.findIndex((x) => x.id === shopId);
  if (idx <= 0) return true;
  return isShopComplete(s, list[idx - 1]!) && s.stars >= shop.unlockStars;
}

/**
 * A built-in city opens like its first shop would on one long street: the city before it has
 * its last shop fully renovated and the player has the stars that first shop asks for. The
 * server opens shops in that same global order (shops/upgrades.ts), so the two agree.
 */
export function isCityOpen(s: PlayerState, city: string, prev: string): boolean {
  const first = cityShops(city)[0];
  const done = cityShops(prev).at(-1);
  return !!first && !!done && isShopComplete(s, done) && s.stars >= first.unlockStars;
}

/** The shop the player is renovating now: first unlocked, incomplete shop in the city. */
export function activeShop(s: PlayerState, city = s.city): ShopDef {
  const list = cityShops(city);
  return list.find((x) => isShopUnlocked(s, x.id) && !isShopComplete(s, x)) ?? list[list.length - 1]!;
}

export function nextUpgrade(s: PlayerState, shopId: string): UpgradeDef | null {
  const shop = shopDef(shopId);
  if (!shop) return null;
  const owned = s.shops[shopId]?.upgrades ?? {};
  return shop.upgrades.find((u) => !(u.slot in owned)) ?? null;
}

export type UpgradeState =
  | { status: 'owned'; style: string }
  | { status: 'locked' }
  | { status: 'ready'; cost: UpgradeCost; rareItem: string | null }
  /** coins and stars are there; the slot's rare item is not (SO-01 restock) */
  | { status: 'rare'; cost: UpgradeCost; rareItem: string }
  | { status: 'short'; cost: UpgradeCost; need: UpgradeCost; rareItem: string | null };

/** The rare item a slot needs that the player does not own, or null. */
export function missingRareItem(s: PlayerState, def: UpgradeDef): string | null {
  return def.rareItem && (s.rareItems[def.rareItem] ?? 0) < 1 ? def.rareItem : null;
}

/** MT-02.2: show exactly what is still needed; never a paywall. */
export function upgradeState(s: PlayerState, shopId: string, slot: string): UpgradeState {
  const shop = shopDef(shopId);
  const def = shop?.upgrades.find((u) => u.slot === slot);
  if (!shop || !def) return { status: 'locked' };
  const owned = s.shops[shopId]?.upgrades?.[slot];
  if (owned) return { status: 'owned', style: owned };
  if (!isShopUnlocked(s, shopId) || nextUpgrade(s, shopId)?.slot !== slot) return { status: 'locked' };
  const cost = { coins: def.coins, stars: def.starsGate };
  const need = upgradeShortfall(cost, { coins: s.coins, stars: s.stars });
  const rare = missingRareItem(s, def);
  if (need.coins > 0 || need.stars > 0) return { status: 'short', cost, need, rareItem: rare };
  if (rare) return { status: 'rare', cost, rareItem: rare };
  return { status: 'ready', cost, rareItem: def.rareItem ?? null };
}

export function featureUnlocked(s: PlayerState, feature: string): boolean {
  const lvl = economy.featureUnlocks[feature];
  return lvl === undefined || s.level >= lvl;
}

/**
 * OVL-03: the level gate is only half of a feature's door. Teams and Events both open at player
 * level 15, and each also sits behind a remote flag. The flag is off in the R1 soft launch and again
 * after a kill switch or a rollback to it, so the intro announces them and has to say they are not
 * here yet while it is.
 *
 * Every `economy.featureUnlocks` key a flag can hide is spelled the same in both places
 * (`dailyPuzzle`, `league`, `teams`, `events` — see those features' manifests) and the rest have no
 * flag at all (`boosters`, `magnet`, `extraSlot`, `friends`), so the intro asks the live config by
 * the unlock key rather than carrying "Coming soon!" inside the copy of a feature that may ship any
 * week. Features that merely unlock ALONGSIDE one of those — the city cup and the war ride on
 * `events` and `teams` — carry flags of their own and are never announced, so they are none of this
 * rule's business. An unlock key with no flag is open the moment its level arrives.
 * (`apps/game/test/features.test.ts` holds that spelling to its word.)
 */
export function unlockPending(feature: string, flags: Record<string, boolean>): boolean {
  return flags[feature] === false;
}

/** Features unlocked by moving from player level `from` to `to` (MT-05.1). */
export function unlocksBetween(from: number, to: number): string[] {
  return Object.entries(economy.featureUnlocks)
    .filter(([, lvl]) => lvl > from && lvl <= to)
    .sort((a, b) => a[1] - b[1])
    .map(([k]) => k);
}
